import { useEffect, useState } from "react"
import { Modal, Form, Input, Select, Tag, Space, Button } from "antd"
import { Toast } from "@/components/Toast"
import { TrainingProjectFormData, TrainingProjectModalProps } from "../types"
import domainLv2Api from "../../domain-lv2/api"
import { DomainLv2 } from "../../domain-lv2/types"
import { ID } from "@/types/common"
import { useDomainLv1 } from "@/hooks/useDomainLv1"

const { TextArea } = Input
const { Option } = Select
const NAME_SPLIT_REGEX = /[\n,，;；]+/

const TrainingProjectModal = ({
  isOpen,
  onClose,
  onSave,
  trainingProject,
  loading = false,
  initialValues,
  subdomains: parentSubdomains,
}: TrainingProjectModalProps) => {
  // 使用全局一级领域数据
  const { domainLv1s } = useDomainLv1()
  // 表单
  const [form] = Form.useForm<TrainingProjectFormData>()
  const [rawNames, setRawNames] = useState("")
  const [parsedNames, setParsedNames] = useState<string[]>([])
  const [nameError, setNameError] = useState<string | null>(null)
  const [nameHint, setNameHint] = useState<string | null>(null)
  // 二级领域列表
  const [subDomains, setSubdomains] = useState<DomainLv2[]>([])
  // 二级领域列表加载状态
  const [subdomainsLoading, setSubdomainsLoading] = useState(false)

  const resetBatchState = () => {
    setRawNames("")
    setParsedNames([])
    setNameError(null)
    setNameHint(null)
  }

  const parseNames = (input: string) => {
    return input
      .split(NAME_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  const handleParse = () => {
    const input = rawNames.trim()
    if (!input) {
      setNameError("请输入项目名称")
      return
    }
    const tokens = rawNames
      .split(NAME_SPLIT_REGEX)
      .map((item) => item.trim())
    const emptyCount = tokens.filter((item) => !item).length
    const inputNames = tokens.filter(Boolean)
    const uniqueInput = Array.from(new Set(inputNames))
    const inputDuplicateCount = inputNames.length - uniqueInput.length
    const existingDuplicateCount = uniqueInput.filter((name) =>
      parsedNames.includes(name)
    ).length
    const nextNames = uniqueInput
    if (nextNames.length === 0) {
      setNameError("请输入有效的项目名称")
      return
    }
    const merged = Array.from(new Set([...parsedNames, ...nextNames]))
    setParsedNames(merged)
    setRawNames("")
    setNameError(null)
    const hints: string[] = []
    if (emptyCount > 0) {
      hints.push(`已忽略 ${emptyCount} 个空行`)
    }
    if (inputDuplicateCount > 0) {
      hints.push(`输入内重复 ${inputDuplicateCount} 个已去重`)
    }
    if (existingDuplicateCount > 0) {
      hints.push(`与已解析重复 ${existingDuplicateCount} 个已去重`)
    }
    setNameHint(hints.length > 0 ? hints.join("；") : null)
  }

  // 加载二级领域数据（根据一级领域ID）
  const loadSubdomains = async (domainId?: ID) => {
    try {
      setSubdomainsLoading(true)
      const res = await domainLv2Api.getList(domainId!)
      setSubdomains(res.data || [])
    } catch (error) {
      Toast.error("加载二级领域数据失败")
      console.error("加载二级领域数据失败:", error)
    } finally {
      setSubdomainsLoading(false)
    }
  }

  // 处理一级领域变化
  const handleDomainChange = (domainId: ID) => {
    // 加载对应的二级领域
    loadSubdomains(domainId)
    // 清空二级领域选择
    form.setFieldValue("domainLv2Id", undefined)
  }

  // 当弹框打开时，根据模式设置表单数据
  useEffect(() => {
    if (!isOpen) return
    resetBatchState()
    setSubdomains(parentSubdomains)
    form.setFieldsValue({
      domainId: initialValues?.domainId,
      domainLv2Id: initialValues?.domainLv2Id,
    })
    // 编辑模式
    if (trainingProject) {
      form.setFieldsValue({
        name: trainingProject.name,
        note: trainingProject.note,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // 处理表单提交
  const handleSubmit = async () => {
    const values = await form.validateFields()
    if (trainingProject) {
      const formData: TrainingProjectFormData = {
        name: values.name?.trim?.() ?? values.name,
        domainLv2Id: values.domainLv2Id,
        note: values.note?.trim?.(),
      }
      await onSave(formData)
      return
    }

    let names = parsedNames
    if (names.length === 0 && rawNames.trim()) {
      names = parseNames(rawNames)
    }
    if (names.length === 0) {
      setNameError("请至少添加一个项目名称")
      return
    }
    const uniqueNames = Array.from(new Set(names))
    if (uniqueNames.length !== names.length) {
      setNameHint(`已自动去重 ${names.length - uniqueNames.length} 个重复名称`)
    }
    const formData: TrainingProjectFormData = {
      names: uniqueNames,
      domainLv2Id: values.domainLv2Id,
      note: values.note?.trim?.(),
    }
    await onSave(formData)
  }

  // 处理取消
  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={trainingProject ? "编辑训练项目" : "新增训练项目"}
      open={isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      destroyOnHidden
      okText="确定"
      cancelText="取消"
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        {trainingProject ? (
          <Form.Item
            label="项目名称"
            name="name"
            rules={[{ required: true, message: "请输入项目名称" }]}
          >
            <Input placeholder="请输入项目名称" maxLength={50} showCount />
          </Form.Item>
        ) : (
          <Form.Item
            label="项目名称（批量粘贴）"
            required
            validateStatus={nameError ? "error" : undefined}
            help={
              nameError ||
              nameHint ||
              "支持换行、逗号分隔；解析后可删除单条名称"
            }
          >
            <div className="flex gap-2">
              <TextArea
                placeholder="例如：追视训练，扫视训练（支持换行/逗号）"
                rows={3}
                value={rawNames}
                onChange={(event) => {
                  setRawNames(event.target.value)
                  if (nameError) setNameError(null)
                }}
                onPressEnter={(event) => {
                  if (event.ctrlKey || event.metaKey) {
                    event.preventDefault()
                    handleParse()
                  }
                }}
              />
              <div className="flex flex-col gap-2">
                <Button type="primary" onClick={handleParse}>
                  解析
                </Button>
                <Button
                  onClick={() => {
                    setRawNames("")
                    setParsedNames([])
                    setNameError(null)
                    setNameHint(null)
                  }}
                  disabled={rawNames.length === 0 && parsedNames.length === 0}
                >
                  清空
                </Button>
              </div>
            </div>
            {parsedNames.length > 0 && (
              <div className="mt-3">
                <div className="text-sm text-gray-500 mb-2">
                  已解析 {parsedNames.length} 个
                </div>
                <Space size={[8, 8]} wrap>
                  {parsedNames.map((name) => (
                    <Tag
                      key={name}
                      closable
                      onClose={(event) => {
                        event.preventDefault()
                        setParsedNames((prev) =>
                          prev.filter((item) => item !== name)
                        )
                      }}
                    >
                      {name}
                    </Tag>
                  ))}
                </Space>
              </div>
            )}
          </Form.Item>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="一级领域"
            name="domainId"
            rules={[{ required: true, message: "请选择一级领域" }]}
          >
            <Select
              placeholder="请选择一级领域"
              onChange={handleDomainChange}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {domainLv1s.map((domain) => (
                <Option key={domain.id} value={domain.id}>
                  {domain.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

        <Form.Item
          label="二级领域"
          name="domainLv2Id"
          rules={[{ required: true, message: "请选择二级领域" }]}
        >
          <Select
            placeholder="请先选择一级领域"
            loading={subdomainsLoading}
            disabled={subDomains.length === 0}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                ?.includes(input.toLowerCase())
            }
          >
            {subDomains.map((subdomain) => (
              <Option key={subdomain.id} value={subdomain.id}>
                {subdomain.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

        <Form.Item label="备注" name="note">
          <TextArea
            placeholder="请输入备注（可选）"
            rows={4}
            maxLength={200}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default TrainingProjectModal
